import * as THREE from 'three'

/**
 * Hidden-line style contour extraction.
 *
 * `THREE.EdgesGeometry` only knows about *crease* edges, so a white cylinder on
 * a white background comes out with two cap ellipses and no sides — the form
 * disappears. Technical illustration needs the view-dependent **silhouette**
 * too: every edge whose two adjacent faces disagree about facing the camera.
 *
 * So each geometry gets a half-edge adjacency table built once and cached; at
 * draw time we filter it against the current view direction. Crease and
 * boundary edges are unconditional, silhouettes are recomputed whenever the
 * camera orbit or an item's rotation changes. Cost is a dot product per edge —
 * a few thousand per frame at worst, and only on change.
 */
export interface ContourEdges {
  /** 6 floats per edge: `ax, ay, az, bx, by, bz`. */
  positions: Float32Array
  /** 6 floats per edge: the two adjacent face normals. */
  normals: Float32Array
  /** 1 when the edge is drawn regardless of view (boundary or crease). */
  unconditional: Uint8Array
  count: number
}

const cache = new Map<string, ContourEdges>()

const WELD_PRECISION = 1e4

interface EdgeRecord {
  ax: number
  ay: number
  az: number
  bx: number
  by: number
  bz: number
  n1x: number
  n1y: number
  n1z: number
  n2x: number
  n2y: number
  n2z: number
  faces: number
}

function vertexKey(x: number, y: number, z: number): string {
  return `${Math.round(x * WELD_PRECISION)},${Math.round(y * WELD_PRECISION)},${Math.round(z * WELD_PRECISION)}`
}

function buildContourEdges(geometry: THREE.BufferGeometry, thresholdDeg: number): ContourEdges {
  const position = geometry.getAttribute('position') as THREE.BufferAttribute | undefined
  if (!position) {
    return { positions: new Float32Array(0), normals: new Float32Array(0), unconditional: new Uint8Array(0), count: 0 }
  }

  const index = geometry.getIndex()
  const triangleCount = (index ? index.count : position.count) / 3
  const cosThreshold = Math.cos(THREE.MathUtils.degToRad(thresholdDeg))

  const edges = new Map<string, EdgeRecord>()

  const ax = new THREE.Vector3()
  const bx = new THREE.Vector3()
  const cx = new THREE.Vector3()
  const ab = new THREE.Vector3()
  const ac = new THREE.Vector3()
  const normal = new THREE.Vector3()

  for (let t = 0; t < triangleCount; t += 1) {
    const i0 = index ? index.getX(t * 3) : t * 3
    const i1 = index ? index.getX(t * 3 + 1) : t * 3 + 1
    const i2 = index ? index.getX(t * 3 + 2) : t * 3 + 2

    ax.fromBufferAttribute(position, i0)
    bx.fromBufferAttribute(position, i1)
    cx.fromBufferAttribute(position, i2)

    ab.subVectors(bx, ax)
    ac.subVectors(cx, ax)
    normal.crossVectors(ab, ac)
    if (normal.lengthSq() < 1e-16) continue // degenerate triangle
    normal.normalize()

    const corners = [ax, bx, cx] as const
    for (let e = 0; e < 3; e += 1) {
      const p = corners[e]!
      const q = corners[(e + 1) % 3]!
      const kp = vertexKey(p.x, p.y, p.z)
      const kq = vertexKey(q.x, q.y, q.z)
      if (kp === kq) continue
      const key = kp < kq ? `${kp}|${kq}` : `${kq}|${kp}`

      const existing = edges.get(key)
      if (existing) {
        existing.faces += 1
        if (existing.faces === 2) {
          existing.n2x = normal.x
          existing.n2y = normal.y
          existing.n2z = normal.z
        }
        continue
      }

      edges.set(key, {
        ax: p.x,
        ay: p.y,
        az: p.z,
        bx: q.x,
        by: q.y,
        bz: q.z,
        n1x: normal.x,
        n1y: normal.y,
        n1z: normal.z,
        n2x: normal.x,
        n2y: normal.y,
        n2z: normal.z,
        faces: 1,
      })
    }
  }

  const count = edges.size
  const positions = new Float32Array(count * 6)
  const normals = new Float32Array(count * 6)
  const unconditional = new Uint8Array(count)

  let i = 0
  for (const edge of edges.values()) {
    const o = i * 6
    positions[o] = edge.ax
    positions[o + 1] = edge.ay
    positions[o + 2] = edge.az
    positions[o + 3] = edge.bx
    positions[o + 4] = edge.by
    positions[o + 5] = edge.bz
    normals[o] = edge.n1x
    normals[o + 1] = edge.n1y
    normals[o + 2] = edge.n1z
    normals[o + 3] = edge.n2x
    normals[o + 4] = edge.n2y
    normals[o + 5] = edge.n2z

    const dot = edge.n1x * edge.n2x + edge.n1y * edge.n2y + edge.n1z * edge.n2z
    // Boundary edges, non-manifold edges and hard creases always draw.
    unconditional[i] = edge.faces !== 2 || dot < cosThreshold ? 1 : 0
    i += 1
  }

  return { positions, normals, unconditional, count }
}

export function contourEdgesFor(geometry: THREE.BufferGeometry, thresholdDeg: number): ContourEdges {
  const key = `${geometry.uuid}:${thresholdDeg}`
  const hit = cache.get(key)
  if (hit) return hit
  const built = buildContourEdges(geometry, thresholdDeg)
  cache.set(key, built)
  return built
}

/* ----------------------------------------------------------- collection */

const _inverseRoot = new THREE.Matrix4()
const _relative = new THREE.Matrix4()
const _normalBasis = new THREE.Matrix3()
const _localView = new THREE.Vector3()
const _a = new THREE.Vector3()
const _b = new THREE.Vector3()

/**
 * Collects the contour of a whole subtree into one flat position buffer,
 * expressed in the subtree's local space.
 *
 * @param viewDirection unit vector pointing *from the camera into the scene*, in world space
 */
export function collectContour(
  root: THREE.Object3D,
  viewDirection: THREE.Vector3,
  defaultThreshold: number,
  out: number[] = [],
): number[] {
  out.length = 0
  root.updateWorldMatrix(false, true)
  _inverseRoot.copy(root.matrixWorld).invert()

  root.traverse((object) => {
    const mesh = object as THREE.Mesh
    if (!mesh.isMesh || !mesh.geometry) return
    if (mesh.userData.outline === false) return
    if (mesh.visible === false) return

    const threshold = (mesh.userData.outlineThreshold as number | undefined) ?? defaultThreshold
    const edges = contourEdgesFor(mesh.geometry, threshold)
    if (edges.count === 0) return

    _relative.multiplyMatrices(_inverseRoot, mesh.matrixWorld)

    // View direction in this mesh's object space (model matrices are orthonormal
    // up to a uniform scale, which normalisation cancels).
    _normalBasis.setFromMatrix4(mesh.matrixWorld).invert()
    _localView.copy(viewDirection).applyMatrix3(_normalBasis).normalize()

    const { positions, normals, unconditional, count } = edges
    for (let i = 0; i < count; i += 1) {
      const o = i * 6
      if (!unconditional[i]) {
        const d1 =
          (normals[o] ?? 0) * _localView.x +
          (normals[o + 1] ?? 0) * _localView.y +
          (normals[o + 2] ?? 0) * _localView.z
        const d2 =
          (normals[o + 3] ?? 0) * _localView.x +
          (normals[o + 4] ?? 0) * _localView.y +
          (normals[o + 5] ?? 0) * _localView.z
        // Same facing on both sides -> interior edge, nothing to draw.
        if (d1 < 0 === d2 < 0) continue
      }

      _a
        .set(positions[o] ?? 0, positions[o + 1] ?? 0, positions[o + 2] ?? 0)
        .applyMatrix4(_relative)
      _b
        .set(positions[o + 3] ?? 0, positions[o + 4] ?? 0, positions[o + 5] ?? 0)
        .applyMatrix4(_relative)
      out.push(_a.x, _a.y, _a.z, _b.x, _b.y, _b.z)
    }
  })

  return out
}
